import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KeyboardDoubleArrowDown, KeyboardDoubleArrowRight } from '@mui/icons-material';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  useTheme
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, ReactNode, memo, Reducer, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import Scrollbar from 'src/components/Scrollbar';
import { actionBarHeight, appBarHeight } from 'src/constants/layout';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { AssignTruckEntity } from 'src/entities/AssignTruck.entity';
import { GarageEntity } from 'src/entities/Garage.entity';
import { FirstVisitChecker } from 'src/entities/PastVisitPlace.entity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { PlanningsOperationEntity } from 'src/entities/PlanningsOperation.entity';
import {
  PlanningsOperationDeliveryByDeliveryIdEntity,
  PlanningsOperationEntitiesWithStatsByDeliveryIdEntity
} from 'src/entities/PlanningsOperationEntitiesWithStatsByDeliveryId.entity';
import { PlanningStatisticsEntity } from 'src/entities/PlanningStatistics.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { PlanningsWorkingStatisticEntity } from 'src/entities/PlanningsWorkingStatistic.entity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { TransferRequestEntity } from 'src/entities/transferRequestEntity';
import { useMutationPlanningTruckDisplayOrders } from 'src/hooks/useQueryPlanningsTrucks';
import { Co2CalculatorModel } from 'src/models/Co2Calculator.model';
import { PlanningsOperationPlace } from 'src/models/PlanningsOperationGroup.model';
import FormDialog from 'src/pages/V2DriversPresenter/FormDialog';
import { default as TruckFormDialog } from 'src/pages/V2TrucksPresenter/FormDialog';
import arrayUtil from 'src/utils/array.util';
import numberUtil from 'src/utils/number.util';
import { compareTrucksByDisplayOrder, resolveSortedTruckIds } from 'src/utils/truckDisplayOrder.util';
import { SelectedStatusVo } from 'src/vo/SelectedStatus.vo';

import PlanningStatisticsPresenter from './PlanningStatistics.presenter';
import PlanningTemporaryTruckDialog from './PlanningTemporaryTruckDialog';
import PlanningTruckPresenter from './PlanningTruck.presenter';
import TruckSelectMenuPresenter from './TruckSelectMenuPresenter';

// 静的な modifier。レンダリングのたびに新しい配列を生成しないようコンポーネント外で定義する。
const dndModifiers = [restrictToVerticalAxis];

type WorkingStatisticsesActions = {
  type: 'update';
  payload: PlanningsWorkingStatisticEntity
}

type SortableTruckItemProps = {
  id: number;
  disabled: boolean;
  children: ReactNode;
}

type SortedTruckIdsState = {
  rangeKey: string;
  ids: number[];
}

const SortableTruckItem: FC<SortableTruckItemProps> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });

  return (
    <Stack
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </Stack>
  );
};

export const workingStatisticsesReducer: Reducer<PlanningsWorkingStatisticEntity[], WorkingStatisticsesActions> = (state, action) => {
  switch (action.type) {
    case 'update': {
      const updateDeliveryId = action.payload.deliveryId;

      return [
        ...state.filter((it) => updateDeliveryId !== it.deliveryId),
        action.payload
      ];
    }
    default:
      return state;
  }
};

type Props = {
  startOn: string;
  endOn: string;
  updateDisplayOrderId: (orderId: number) => void;
  mutateDeleteOrdersOperations: (requestOrderIds: number[]) => void;
  openTransferDialog: (entities: TransferRequestEntity) => void;
  mutateCloneLastWeekShifts: (date: string) => void;
  mutateDeleteShiftsOperations: (deliveryIds: number[]) => void;
  isLoading: boolean;
  garageData: GarageEntity[];
  truckEntities: PlanningsTruckEntity[];
  driverEntities: PlanningsDriverEntity[];
  deliveryEntities: PlanningsDeliveryEntity[];
  operationEntities: PlanningsOperationEntity[];
  swapRequest: (swapFromDeliveryId: number, swapToDeliveryId: number) => void;
  groupEntities: PlanningsGroupEntity[];
  planningsOperationEntitiesWithStatsByDeliveryIdEntity: PlanningsOperationEntitiesWithStatsByDeliveryIdEntity;
  sendOperationMail: () => void;
  refreshOperationStatuses: () => void;
  lastOperationStatusUpdated: string;
  resetDrivers: () => void;
  currentHistoryVersion: string | undefined;
  isFirstVisitPlace: FirstVisitChecker;
  latestAllocateHistory: AllocateHistoryEntity | undefined;
  selectPrintButtonOnClick: () => void;
  requestSingleAlgorithmPlanning: (deliveryId: number, orderOperationIdsForSort: number[], deleteOrderIdsFromOperations: number[], orderOperationCycleIndexes?: { [key: number]: number }) => void;
  selectedCycleIndexes: SelectedCycle[];
  updateSelectedCycleIndexes: (deliveryId: number, cycleIndexes: number[]) => void;
  planningsOperationDeliveryByDeliveryIdEntity: PlanningsOperationDeliveryByDeliveryIdEntity;
  resetEditPlaces: () => void;
  updateEditPlaces: (deliveryId: number, cycleIndex: number, places: PlanningsOperationPlace[]) => void;
  addEmptyCycle: (deliveryId: number) => void;
  removeEmptyCycle: (deliveryId: number, cycleIndex: number) => void;
  addDeliveryElementRef: (deliveryId: number, element: HTMLElement) => void;
  assignTruckToTemporaryDeliveryRequest: (entity: AssignTruckEntity) => void;
  assignTruckQueue: AssignTruckEntity[];
  currentAssigning: AssignTruckEntity;
  allDeliveriesLoaded: boolean;
  deleteTemporaryDelivery: (deliveryId: number) => void;
  onConfirmDeliveries: (deliveryIds: number[]) => void;
  onUnconfirmDeliveries: (deliveryIds: number[]) => void;
  isConfirming: boolean;
  isUnconfirming: boolean;
  billingTotal: number;
  paymentTotal: number;
  setTariffCalculationDialogIsOpen: (open: boolean) => void;
}

const PlanningTrucksPresenter: FC<Props> = memo(
  (
    {
      startOn,
      endOn,
      updateDisplayOrderId,
      mutateDeleteOrdersOperations,
      openTransferDialog,
      mutateCloneLastWeekShifts,
      mutateDeleteShiftsOperations,
      isLoading,
      garageData,
      truckEntities,
      driverEntities,
      deliveryEntities,
      operationEntities,
      swapRequest,
      groupEntities,
      planningsOperationEntitiesWithStatsByDeliveryIdEntity,
      sendOperationMail,
      refreshOperationStatuses,
      lastOperationStatusUpdated,
      resetDrivers,
      currentHistoryVersion,
      isFirstVisitPlace,
      latestAllocateHistory,
      selectPrintButtonOnClick,
      requestSingleAlgorithmPlanning,
      selectedCycleIndexes,
      updateSelectedCycleIndexes,
      planningsOperationDeliveryByDeliveryIdEntity,
      resetEditPlaces,
      updateEditPlaces,
      addEmptyCycle,
      removeEmptyCycle,
      addDeliveryElementRef,
      assignTruckToTemporaryDeliveryRequest,
      assignTruckQueue,
      currentAssigning,
      allDeliveriesLoaded,
      deleteTemporaryDelivery,
      onConfirmDeliveries,
      onUnconfirmDeliveries,
      isConfirming,
      isUnconfirming,
      billingTotal,
      paymentTotal,
      setTariffCalculationDialogIsOpen,
    }
  ) => {
    const context = useContext(LicenseContext);
    const plansContext = useContext(PlansContext);
    const { truckDisplayStatus, selectedGroupEntities } = plansContext;
    const theme = useTheme();

    const selectedStatusInitialState = 'none';

    const [selectedStatus, setSelectedStatus] = useState<SelectedStatusVo>(selectedStatusInitialState);
    const [planningStatisticsEntity, setPlanningStatisticsEntity] = useState<PlanningStatisticsEntity | undefined>(undefined);
    const [workingStatisticses, dispatchWorkingStatisticses] = useReducer(workingStatisticsesReducer, []);
    const [driverApp, setDriverApp] = useState<boolean>(false);
    const [co2Emissions, setCo2Emissions] = useState<number>(0);
    const [expandAll, setExpandAll] = useState(false);
    const selectedDeliveryIds = useMemo(() => selectedCycleIndexes.map((it) => it.deliveryId), [selectedCycleIndexes]);
    const [sortedTrucks, setSortedTrucks] = useState<PlanningsTruckEntity[]>([]);
    const { enqueueSnackbar } = useSnackbar();
    const canSortTruckDisplayOrder = startOn === endOn;
    const truckDisplayOrderMutation = useMutationPlanningTruckDisplayOrders(startOn, endOn);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
    const planningRangeKey = `${startOn}:${endOn}`;

    const hasOperationTruckIds = useMemo(() => deliveryEntities.filter(({ operations: { length } }) => length).map(({ truckId }) => truckId), [deliveryEntities]);

    useEffect(() => {
      plansContext.setAllDeliverySelected(selectedStatus === 'every');
    }, [selectedStatus]);

    const updateWorkingStatisticses = useCallback((entity: PlanningsWorkingStatisticEntity) => {
      dispatchWorkingStatisticses({
        type: 'update',
        payload: entity
      });
    }, []);

    const cloneButtonOnClick = () => {
      mutateCloneLastWeekShifts(startOn);
    };
    const resetButtonOnChange = useCallback(() => {
      ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: 'すべて未割り当て' });
      mutateDeleteShiftsOperations(selectedDeliveryIds);
    }, [mutateDeleteShiftsOperations, selectedDeliveryIds]);

    const swapButtonOnClick = useCallback(() => {
      ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '配車計画入れ替え' });
      if (selectedDeliveryIds.length !== 2) return;

      swapRequest(
        selectedDeliveryIds[0],
        selectedDeliveryIds[1],
      );
    }, [selectedDeliveryIds, swapRequest]);

    const canDisplay = useCallback((delivery: PlanningsDeliveryEntity) => {
      switch (truckDisplayStatus) {
        case 'すべて表示':
          return true;
        case '配車済み車両のみ表示':
          return hasOperationTruckIds.includes(delivery.truckId);
        case '未配車の車両のみ表示':
          return !hasOperationTruckIds.includes(delivery.truckId);
        case '選択中のみ表示する':
          return selectedDeliveryIds.includes(delivery.id);
        default:
          return true;
      }
    }, [hasOperationTruckIds, selectedDeliveryIds, truckDisplayStatus]);

    const [displayTruckIdsByGroup, setDisplayTruckIdsByGroup] = useState<number[]>([]);
    useEffect(() => {
      if (!(selectedGroupEntities && groupEntities && truckEntities)) return;

      // 同内容なら前回参照を返し、不要な再レンダー(setState ループ)を避ける
      const ids = selectedGroupEntities
        ? Array.from(new Set(selectedGroupEntities.filter((it) => it).flatMap((it) => it.truckIds)))
        : [];
      setDisplayTruckIdsByGroup((prev) => (prev.length === ids.length && prev.every((v, i) => v === ids[i]) ? prev : ids));
    }, [groupEntities, selectedGroupEntities, truckEntities]);

    const checkboxOnChange = useCallback(() => {
      const deliveriesOnGroup = deliveryEntities
        .filter((it) => displayTruckIdsByGroup.includes(it.truckId));
      const deliveryIds = deliveriesOnGroup.map((it) => it.id);
      const deliveryIdsOnlyDisplay = deliveriesOnGroup
        .filter((it) => canDisplay(it))
        .map((it) => it.id);

      ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: `車両すべて選択 ${selectedStatus === 'every' ? 'OFF' : 'ON'}` });
      if (selectedStatus === 'every') {
        deliveryIds.forEach((it) => {
          updateSelectedCycleIndexes(it, []);
        });
        return;
      }

      deliveryIdsOnlyDisplay.forEach((it) => {
        const delivery = planningsOperationDeliveryByDeliveryIdEntity[it];
        if (delivery) {
          updateSelectedCycleIndexes(it, delivery.cycles.map((cycle) => cycle.cycleIndex));
        }
      });
    }, [deliveryEntities, selectedStatus, displayTruckIdsByGroup, canDisplay, updateSelectedCycleIndexes, planningsOperationDeliveryByDeliveryIdEntity]);

    const [editDriverId, setEditDriverId] = useState<number>();
    const driverDialogIsOpen = Boolean(editDriverId);

    const [editTruckId, setEditTruckId] = useState<number>();
    const truckDialogIsOpen = Boolean(editTruckId);

    const closeDriverEditDialog = useCallback(() => {
      setEditDriverId(null);
    }, []);

    const setDriverEditDialogIsOpen = useCallback((open: boolean) => {
      if (open) {
        setEditDriverId(editDriverId);
      } else {
        setEditDriverId(null);
      }
    }, [editDriverId]);

    const updateEditDriverId = useCallback((driverId: number) => {
      setEditDriverId(driverId);
    }, []);

    const updateEditTruckId = useCallback((truckId: number) => {
      setEditTruckId(truckId);
    }, []);

    const closeTruckEditDialog = useCallback(() => {
      setEditTruckId(null);
    }, []);

    const driverEditDialogMemo = useMemo(() => {
      const maybe: PlanningsDriverEntity | undefined = (driverEntities ?? []).find((it) => it.id === editDriverId);

      if (!maybe) return null;

      return (
        <FormDialog
          dialogIsOpen={driverDialogIsOpen}
          toggleDialogIsOpen={closeDriverEditDialog}
          setDialogIsOpen={setDriverEditDialogIsOpen}
          resetDrivers={resetDrivers}
          driverId={editDriverId}
        />
      );
    }, [driverEntities, driverDialogIsOpen, closeDriverEditDialog, setDriverEditDialogIsOpen, resetDrivers, editDriverId]);

    const truckEditDialogMemo = useMemo(() => {
      const maybe: PlanningsTruckEntity | undefined = (truckEntities ?? []).find((it) => it.id === editTruckId);

      if (!maybe) return null;

      return (
        <TruckFormDialog
          isOpen={truckDialogIsOpen}
          onClose={closeTruckEditDialog}
          truckId={editTruckId}
        />
      );
    }, [closeTruckEditDialog, editTruckId, truckDialogIsOpen, truckEntities]);

    useMemo(() => {
      if (!context) { return; }
      if (!context.config) { return; }
      setDriverApp(context.config.use_driver_app);
    }, [context]);

    const swapButtonIsDisabled: boolean = useMemo(() => {
      const selectDeliveries = selectedDeliveryIds.map((it) => deliveryEntities.find((ent) => ent.id === it)).filter((maybe) => maybe);

      return [
        ...selectDeliveries.flatMap((it) => it.operations),
      ].length === 0;
    }, [selectedDeliveryIds, deliveryEntities]);

    const deliveriesOnTheTruckMap = useMemo(() => {
      const map = new Map<number, PlanningsDeliveryEntity[]>();
      deliveryEntities.forEach((it) => {
        const tmp = map.get(it.truckId) || [];
        tmp.push(it);
        map.set(it.truckId, tmp);
      });
      return map;
    }, [deliveryEntities]);

    const [sortedTruckIdsState, setSortedTruckIdsState] = useState<SortedTruckIdsState>({ rangeKey: '', ids: [] });
    const sortedTruckIds = useMemo(
      () => (sortedTruckIdsState.rangeKey === planningRangeKey ? sortedTruckIdsState.ids : []),
      [planningRangeKey, sortedTruckIdsState]
    );
    const updateSortedTruckIds = useCallback((ids: number[]) => {
      setSortedTruckIdsState({ rangeKey: planningRangeKey, ids });
    }, [planningRangeKey]);

    const stickyHeaderRef = useRef<HTMLDivElement>(null);
    const [stickyHeaderHeight, setStickyHeaderHeight] = useState<number>(280);

    useEffect(() => {
      if (!stickyHeaderRef.current) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setStickyHeaderHeight(entry.contentRect.height);
        }
      });

      resizeObserver.observe(stickyHeaderRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    useEffect(() => {
      const drivers = driverEntities ?? [];
      const trucks = truckEntities ?? [];
      if (trucks.length === 0) return;

      if (sortedTruckIds.length > 0) {
        const sorted = trucks.slice().sort((a, b) => {
          const aIndex = sortedTruckIds.indexOf(a.id);
          const bIndex = sortedTruckIds.indexOf(b.id);
          // 新規追加された truck が複数あって両方とも sortedTruckIds に未登録の場合、
          // 常に固定値を返すと strict weak ordering 違反になる。
          // API と同じ規則 (displayOrder bucket → sortOrder → id) でフォールバックする。
          if (aIndex === -1 && bIndex === -1) return compareTrucksByDisplayOrder(a, b);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
        // 同じ並び(id順)なら前回参照を返し、不要な再レンダー(setState ループ)を避ける
      setSortedTrucks((prev) => (prev.length === sorted.length && prev.every((t, i) => t.id === sorted[i].id) ? prev : sorted));
        return;
      }

      // API (api/v3/plannings/:on/trucks) と同じ規則: displayOrder bucket → sortOrder → id。
      // それでも tie の場合は driver.sortOrder で二次比較する。
      const MAX_SORT_ORDER = 999999;
      const sorted = trucks.slice().sort((a, b) => {
        const truckCmp = compareTrucksByDisplayOrder(a, b);
        if (truckCmp !== 0) return truckCmp;
        const aDelivery = deliveryEntities.find((it) => it.truckId === a.id);
        const bDelivery = deliveryEntities.find((it) => it.truckId === b.id);
        const aDriver = drivers.find((it) => it.id === aDelivery?.driverId);
        const bDriver = drivers.find((it) => it.id === bDelivery?.driverId);
        return (aDriver?.sortOrder ?? MAX_SORT_ORDER) - (bDriver?.sortOrder ?? MAX_SORT_ORDER);
      });
      // 同じ並び(id順)なら前回参照を返し、不要な再レンダー(setState ループ)を避ける
      setSortedTrucks((prev) => (prev.length === sorted.length && prev.every((t, i) => t.id === sorted[i].id) ? prev : sorted));
    }, [deliveryEntities, driverEntities, truckEntities, sortedTruckIds]);

    useEffect(() => {
      if (sortedTrucks.length === 0 || !allDeliveriesLoaded) return;

      // ドラッグで確定した表示順 (sortedTruckIds) を現在の車両構成に同期する。件数比較だと
      // 「1台削除 + 1台追加」で件数が変わらないケースを取りこぼすため resolveSortedTruckIds
      // (内部で集合差分) を使う。未ドラッグ時やトラック未ロード/クリア中 (truckEntities が空) は
      // null が返り同期をスキップする。後者をしないと、stale な sortedTrucks と空の truckEntities
      // を突合し続けて sortedTruckIds が「空 ↔ 前回値」を往復する無限再レンダー (#2344) に陥る。
      const nextIds = resolveSortedTruckIds({
        storedIds: sortedTruckIds,
        currentTruckIds: truckEntities.map((it) => it.id),
      });
      if (nextIds !== null) {
        setSortedTruckIdsState({ rangeKey: planningRangeKey, ids: nextIds });
      }
    }, [sortedTruckIds, sortedTrucks, allDeliveriesLoaded, truckEntities, planningRangeKey]);

    const handleTruckDragEnd = useCallback((event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      if (!canSortTruckDisplayOrder || truckDisplayOrderMutation.isPending) return;

      const oldIndex = sortedTrucks.findIndex((truck) => truck.id === active.id);
      const newIndex = sortedTrucks.findIndex((truck) => truck.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(sortedTrucks, oldIndex, newIndex);
      const nextIds = next.map((truck) => truck.id);
      setSortedTrucks(next);
      updateSortedTruckIds(nextIds);

      truckDisplayOrderMutation.mutate(nextIds, {
        onError: () => {
          setSortedTruckIdsState({ rangeKey: '', ids: [] });
          enqueueSnackbar('車両表示順の保存に失敗しました', { variant: 'error' });
        }
      });
    }, [canSortTruckDisplayOrder, enqueueSnackbar, sortedTrucks, truckDisplayOrderMutation, updateSortedTruckIds]);

    const canRenderTruckRow = useCallback((truck: PlanningsTruckEntity, hasAnyDelivery: boolean) => {
      const deliveriesOnTheTruck = deliveriesOnTheTruckMap.get(truck.id) || [];
      return !(hasAnyDelivery && deliveriesOnTheTruck.length === 0);
    }, [deliveriesOnTheTruckMap]);

    const [temporaryTruckDialogOpen, setTemporaryTruckDialogOpen] = useState(false);

    const trucksMemo = useMemo(() => {
      if (!garageData) return '';

      const visibleTrucks = sortedTrucks.filter((it) => displayTruckIdsByGroup.includes(it.id));
      const hasAnyDelivery = deliveryEntities.length > 0;
      const sortableTrucks = visibleTrucks.filter((truck) => canRenderTruckRow(truck, hasAnyDelivery));

      const truckRows = sortableTrucks.map((truck) => {
        const deliveriesOnTheTruck = deliveriesOnTheTruckMap.get(truck.id) || [];

        let display = '';
        if (
          deliveriesOnTheTruck.length > 0 &&
          deliveriesOnTheTruck.every((it) => !canDisplay(it))
        ) {
          display = 'none';
        }
        const garage = garageData.find((it) => it.id === truck.garageId);

        return (
          <SortableTruckItem
            id={truck.id}
            disabled={!canSortTruckDisplayOrder || isLoading || truckDisplayOrderMutation.isPending}
            key={['PlanningTrucksPresenter', 'truckMemo', truck.licensePlateValue].join('-')}
          >
            <Stack sx={{ display }}>
              <PlanningTruckPresenter
                key={[
                  'PlanningTrucksPresenter',
                  'PlanningTruckPresenter',
                  truck.id
                ].join('-')}
                entity={truck}
                garage={garage}
                garageData={garageData}
                isLoading={isLoading}
                driverEntities={driverEntities}
                deliveryEntities={deliveryEntities}
                operationEntities={operationEntities}
                updateDisplayOrderId={updateDisplayOrderId}
                mutateDeleteOrdersOperations={mutateDeleteOrdersOperations}
                openTransferDialog={openTransferDialog}
                truckEntities={truckEntities}
                updateWorkingStatisticses={updateWorkingStatisticses}
                currentHistoryVersion={currentHistoryVersion}
                updateEditDriverId={updateEditDriverId}
                deliveriesOnTheTruck={deliveriesOnTheTruck}
                isFirstVisitPlace={isFirstVisitPlace}
                latestAllocateHistory={latestAllocateHistory}
                expandAll={expandAll}
                requestSingleAlgorithmPlanning={requestSingleAlgorithmPlanning}
                startOn={startOn}
                endOn={endOn}
                selectedCycleIndexes={selectedCycleIndexes}
                updateSelectedCycleIndexes={updateSelectedCycleIndexes}
                planningsOperationDeliveryByDeliveryIdEntity={planningsOperationDeliveryByDeliveryIdEntity}
                resetEditPlaces={resetEditPlaces}
                updateEditPlaces={updateEditPlaces}
                addEmptyCycle={addEmptyCycle}
                removeEmptyCycle={removeEmptyCycle}
                addDeliveryElementRef={addDeliveryElementRef}
                assignTruckToTemporaryDeliveryRequest={assignTruckToTemporaryDeliveryRequest}
                assignTruckQueue={assignTruckQueue}
                currentAssigning={currentAssigning}
                updateEditTruckId={updateEditTruckId}
                deleteTemporaryDelivery={deleteTemporaryDelivery}
              />
            </Stack>
          </SortableTruckItem>
        );
      });

      return (
        <Stack
          pt={0.5}
          pb={36}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleTruckDragEnd}
            modifiers={dndModifiers}
          >
            <SortableContext items={sortableTrucks.map((truck) => truck.id)} strategy={verticalListSortingStrategy}>
              {truckRows}
            </SortableContext>
          </DndContext>
          <Divider />
          <Stack>
            <Button onClick={() => setTemporaryTruckDialogOpen(true)}>
              仮車両追加＋
            </Button>
            <PlanningTemporaryTruckDialog
              startOn={startOn}
              endOn={endOn}
              temporaryTruckDialogOpen={temporaryTruckDialogOpen}
              setTemporaryTruckDialogOpen={setTemporaryTruckDialogOpen}
            />
          </Stack>
        </Stack>
      );
    }, [garageData, sortedTrucks, displayTruckIdsByGroup, deliveriesOnTheTruckMap, canRenderTruckRow, canSortTruckDisplayOrder, isLoading, truckDisplayOrderMutation.isPending, driverEntities, deliveryEntities, operationEntities, updateDisplayOrderId, mutateDeleteOrdersOperations, openTransferDialog, truckEntities, updateWorkingStatisticses, currentHistoryVersion, updateEditDriverId, isFirstVisitPlace, latestAllocateHistory, expandAll, requestSingleAlgorithmPlanning, startOn, endOn, selectedCycleIndexes, updateSelectedCycleIndexes, planningsOperationDeliveryByDeliveryIdEntity, resetEditPlaces, updateEditPlaces, addEmptyCycle, removeEmptyCycle, addDeliveryElementRef, canDisplay, temporaryTruckDialogOpen, assignTruckToTemporaryDeliveryRequest, assignTruckQueue, currentAssigning, updateEditTruckId, deleteTemporaryDelivery, sensors, handleTruckDragEnd]);

    const planningStatisticsPresenterMemo = useMemo(() => (
      <Stack
        py={0.5}
        pl={1.5}
      >
        <PlanningStatisticsPresenter
          planningStatistics={planningStatisticsEntity}
          refreshOperationStatuses={refreshOperationStatuses}
          lastOperationStatusUpdated={lastOperationStatusUpdated}
          isLoading={isLoading}
          currentHistoryVersion={currentHistoryVersion}
          driverApp={driverApp}
          co2Emissions={co2Emissions}
          selectPrintButtonOnClick={selectPrintButtonOnClick}
          sendOperationMail={sendOperationMail}
          selectedDeliveryIds={selectedDeliveryIds}
          onConfirmDeliveries={onConfirmDeliveries}
          onUnconfirmDeliveries={onUnconfirmDeliveries}
          isConfirming={isConfirming}
          isUnconfirming={isUnconfirming}
          billing={billingTotal}
          payment={paymentTotal}
          setTariffCalculationDialogIsOpen={setTariffCalculationDialogIsOpen}
        />
      </Stack>
    ), [planningStatisticsEntity, refreshOperationStatuses, lastOperationStatusUpdated, isLoading, currentHistoryVersion, driverApp, co2Emissions, selectPrintButtonOnClick, sendOperationMail, selectedDeliveryIds, onConfirmDeliveries, onUnconfirmDeliveries, isConfirming, isUnconfirming, billingTotal, paymentTotal, setTariffCalculationDialogIsOpen]);

    useEffect(() => {
      if (!truckEntities) {
        setCo2Emissions(0);
        return;
      }
      if (!deliveryEntities) {
        setCo2Emissions(0);
        return;
      }

      const arrayOfCo2Emissions = deliveryEntities.filter((delivery) => delivery.operations.length).map((delivery) => {
        const truck = truckEntities.find((it) => it.id === delivery.truckId);

        if (!truck) {
          return 0;
        }

        const distanceMms = [
          ...delivery.operations.map((it) => it.drivingDistanceMm),
          delivery.arrivalDistanceMm
        ].filter((maybe) => maybe);

        const distanceKms = distanceMms.map((it) => numberUtil.convertFromMmToKm(it));

        const distanceKm = distanceKms.reduce((a, b) => a + b, 0);

        const loadingCapacityGram = truck.maximumLoadingCapacityWeightForCalculation;

        const loadingCapacityKg = numberUtil.convertFromGramToKg(loadingCapacityGram);

        const model = new Co2CalculatorModel(distanceKm, loadingCapacityKg);

        return model.performCalculation();
      });

      setCo2Emissions(
        arrayOfCo2Emissions.reduce((a, b) => a + b, 0)
      );
    }, [truckEntities, deliveryEntities]);

    useEffect(() => {
      if (!operationEntities.length && truckDisplayStatus !== 'すべて表示') {
        plansContext.setTruckDisplayStatus('すべて表示');
      }
    }, [operationEntities, truckDisplayStatus]);

    useEffect(() => {
      const deliveryIds = deliveryEntities
        .filter((it) => displayTruckIdsByGroup.includes(it.truckId))
        .filter((it) => canDisplay(it))
        .map((it) => it.id);

      if (!selectedDeliveryIds.length) {
        setSelectedStatus(
          'none'
        );

        return;
      }
      if (deliveryIds.every((it) => selectedDeliveryIds.includes(it))) {
        setSelectedStatus(
          'every'
        );

        return;
      }
      if (deliveryEntities.some((it) => selectedDeliveryIds.includes(it.id))) {
        setSelectedStatus(
          'some'
        );
      }
    }, [selectedDeliveryIds, deliveryEntities, truckDisplayStatus, canDisplay, displayTruckIdsByGroup]);

    useEffect(() => {
      if (![deliveryEntities, truckEntities, operationEntities, planningsOperationEntitiesWithStatsByDeliveryIdEntity, workingStatisticses].every((maybe) => maybe)) return;

      const groupedEntities = planningsOperationEntitiesWithStatsByDeliveryIdEntity;
      const totalMaxQuantity = Object
        .values(groupedEntities)
        .map((entities) => entities.map((entity) => entity.currentQuantity))
        .filter((maybeEmpty) => !!maybeEmpty.length)
        .map((maxes) => Math.max(...maxes))
        .reduce((prev, current) => prev + current, 0);
      const totalMaxWeightG = Object
        .values(groupedEntities)
        .map((entities: (PlanningsOperationEntity & { currentVolumeMm3: number; currentWeightG: number })[]) => entities.map((entity) => entity.currentWeightG))
        .filter((maybeEmpty) => !!maybeEmpty.length)
        .map((maxes) => Math.max(...maxes))
        .reduce((prev, current) => prev + current, 0);
      const totalMaxVolumeMm3 = Object
        .values(groupedEntities)
        .map((entities: (PlanningsOperationEntity & { currentVolumeMm3: number; currentWeightG: number })[]) => entities.map((entity) => entity.currentVolumeMm3))
        .filter((maybeEmpty) => !!maybeEmpty.length)
        .map((maxes) => Math.max(...maxes))
        .reduce((prev, current) => prev + current, 0);
      const haveOperationDeliveryEntities: PlanningsDeliveryEntity[] = arrayUtil
        .uniq<number>(operationEntities.map((it) => it.shiftTruckId))
        .map((it) => deliveryEntities.find((ent) => ent.id === it))
        .filter((maybe) => maybe);
      const haveOperationTruckEntities = truckEntities
        .filter(({ id }) => haveOperationDeliveryEntities.map(({ truckId }) => truckId).includes(id));
      const haveOperationTruckTotalCapacityWeightG = haveOperationTruckEntities
        .map(({ maximumLoadingCapacityWeightForCalculation }) => maximumLoadingCapacityWeightForCalculation)
        .filter((maybe) => maybe)
        .reduce((prev, current) => prev + current, 0);
      const haveOperationTruckTotalCapacityVolumeMm3 = haveOperationTruckEntities
        .map(({ loadingPlatformVolumeMm3 }) => loadingPlatformVolumeMm3)
        .filter((maybe) => maybe)
        .reduce((prev, current) => prev + current, 0);
      const haveOperationTruckTotalCapacityQuantity = haveOperationDeliveryEntities
        .map((delivery) => delivery.legRunEntity?.maximumLoadingCapacityQuantity)
        .filter((maybe) => maybe)
        .reduce((prev, current) => prev + current, 0);
      const existOrderOperations = Object
        .values(operationEntities)
        .flat();
      const haveOperationDeliveryTotalDistanceMm = haveOperationDeliveryEntities.length
        ? existOrderOperations
          .map((it) => it.drivingDistanceMm)
          .reduce((prev, current) => prev + current, 0)
        + haveOperationDeliveryEntities
          .map((it) => it?.arrivalDistanceMm)
          .filter((maybe) => maybe)
          .reduce((prev, current) => prev + current, 0)
        : 0;
      const loadingOperations = operationEntities.filter((it) => it.action === '積');

      const totalPlanMinutes = workingStatisticses
        .filter((it) => it.operationCount)
        .map((it) => it.plannedMinutes)
        .reduce((prev, current) => prev + current, 0);
      const totalWorkMinutes = workingStatisticses
        .filter((it) => it.operationCount)
        .map((it) => it.workMinutes)
        .reduce((prev, current) => prev + current, 0);

      const totalOrdersCount = loadingOperations.length;
      const totalLoadQuantity = loadingOperations
        .map((it) => it.itemCount)
        .filter((maybe) => maybe)
        .reduce((prev, current) => prev + current, 0);
      const totalLoadWeightG = loadingOperations
        .map((it) => it.itemTotalWeightForCalculation)
        .filter((maybe) => maybe)
        .reduce((prev, current) => prev + current, 0);
      const totalLoadVolumeMm3 = loadingOperations
        .map((it) => it.itemTotalVolumeMm3)
        .filter((maybe) => maybe)
        .reduce((prev, current) => prev + current, 0);
      const loadCapacityQuantity = haveOperationTruckTotalCapacityQuantity;
      const loadCapacityWeightG = haveOperationTruckTotalCapacityWeightG;
      const loadCapacityVolumeMm3 = haveOperationTruckTotalCapacityVolumeMm3;
      const totalMaxLoadQuantity = totalMaxQuantity;
      const totalMaxLoadWeightG = totalMaxWeightG;
      const totalMaxLoadVolumeMm3 = totalMaxVolumeMm3;
      const totalDistanceMm = haveOperationDeliveryTotalDistanceMm;

      setPlanningStatisticsEntity((prev) => {
        if (prev
            && prev.totalOrdersCount === totalOrdersCount
            && prev.totalLoadQuantity === totalLoadQuantity
            && prev.totalLoadWeightG === totalLoadWeightG
            && prev.totalLoadVolumeMm3 === totalLoadVolumeMm3
            && prev.totalMaxLoadQuantity === totalMaxLoadQuantity
            && prev.totalMaxLoadWeightG === totalMaxLoadWeightG
            && prev.totalMaxLoadVolumeMm3 === totalMaxLoadVolumeMm3
            && prev.loadCapacityQuantity === loadCapacityQuantity
            && prev.loadCapacityWeightG === loadCapacityWeightG
            && prev.loadCapacityVolumeMm3 === loadCapacityVolumeMm3
            && prev.totalPlanMinutes === totalPlanMinutes
            && prev.totalWorkMinutes === totalWorkMinutes
            && prev.totalDistanceMm === totalDistanceMm
        ) {
            return prev;
        }
        return {
          totalOrdersCount,
          totalLoadQuantity,
          totalLoadWeightG,
          totalLoadVolumeMm3,
          totalMaxLoadQuantity,
          totalMaxLoadWeightG,
          totalMaxLoadVolumeMm3,
          loadCapacityQuantity,
          loadCapacityWeightG,
          loadCapacityVolumeMm3,
          totalPlanMinutes,
          totalWorkMinutes,
          totalDistanceMm
        };
      });
    }, [deliveryEntities, truckEntities, operationEntities, planningsOperationEntitiesWithStatsByDeliveryIdEntity, workingStatisticses]);

    const [resetTooltip, setResetTooltip] = useState<string>('');
    const [swapTooltip, setSwapTooltip] = useState<string>('');
    useEffect(() => {
      if (context.config?.selected_company_id === 0) {
        setResetTooltip('事業所を選択してください');
        setSwapTooltip('事業所を選択してください');
      } else {
        setResetTooltip('選択した勤務計画の配送計画をすべて未割り当てにする');
        setSwapTooltip('選択した２つの勤務計画の配送計画を入れ替える');
      }
    }, [context.config?.selected_company_id]);

    const selectedDeliveryIdsMemo = useMemo(() => (
      <Badge
        color="primary"
        badgeContent={selectedDeliveryIds.length}
        max={10000}
      >
        <Tooltip
          title={
            selectedStatus === 'every' ? 'すべて選択解除する' : 'すべて選択する'
          }
          arrow
        >
          <Checkbox
            size="small"
            onChange={checkboxOnChange}
            checked={selectedStatus === 'every'}
            indeterminate={selectedStatus === 'some'}
            disabled={isLoading}
            sx={{
              m: 0,
              p: 0.3,
            }}
          />
        </Tooltip>
      </Badge>
    ), [checkboxOnChange, isLoading, selectedDeliveryIds.length, selectedStatus]);

    const resetButtonMemo = useMemo(() => (
      <Tooltip
        title={resetTooltip}
        arrow
      >
        <span>
          <IconButton
            size="small"
            disabled={isLoading || !!currentHistoryVersion || (context.config?.selected_company_id === 0)}
            onClick={resetButtonOnChange}
          >
            <RotateLeftRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>
    ), [context.config?.selected_company_id, currentHistoryVersion, isLoading, resetButtonOnChange, resetTooltip]);

    const swapButtonMemo = useMemo(() => (
      <Tooltip
        title={swapTooltip}
        arrow
      >
        <span>
          <IconButton
            size="small"
            disabled={swapButtonIsDisabled || !(selectedDeliveryIds.length === 2) || isLoading || !!currentHistoryVersion || (context.config?.selected_company_id === 0)}
            onClick={swapButtonOnClick}
          >
            <SwapHorizRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>
    ), [context.config?.selected_company_id, currentHistoryVersion, isLoading, selectedDeliveryIds.length, swapButtonIsDisabled, swapButtonOnClick, swapTooltip]);

    const onChangeExpandAll = (value: boolean) => {
      ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: `車両リスト ${value ? '開く' : '閉じる'}` });
      setExpandAll(value);
    };

    return (
      <Stack>
        <Stack
          ref={stickyHeaderRef}
          sx={{
            position: 'sticky',
            // display: 'block',
            top: 0,
            zIndex: 1,
          }}
        >
          <Paper
            square
          >
            {planningStatisticsPresenterMemo}
          </Paper>
          <Paper
            square
            sx={{
              p: 0,
              m: 0,
              borderBottom: 1,
              borderBottomColor: theme.colors.secondary.light,
            }}
          >
            <Stack
              p={1}
            >
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
              >
                {selectedDeliveryIdsMemo}
                {(selectedDeliveryIds.length > 0) && (
                  <>
                    {resetButtonMemo}
                    {swapButtonMemo}
                  </>
                )}
                <TruckSelectMenuPresenter
                  truckDisplayStatus={truckDisplayStatus}
                  groupEntities={groupEntities}
                />
                {deliveryEntities.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ width: '30px' }}
                    onClick={() => onChangeExpandAll(!expandAll)}
                  >
                    {expandAll ? <KeyboardDoubleArrowDown /> : <KeyboardDoubleArrowRight />}
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
        <Scrollbar
          style={{
            height: `calc(100vh - ${stickyHeaderHeight + 1 + appBarHeight + actionBarHeight}px)`
          }}
        >
          {trucksMemo}
        </Scrollbar>
        {driverEditDialogMemo}
        {truckEditDialogMemo}
      </Stack>
    );
  }
);

export default PlanningTrucksPresenter;
